import React from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { CheckCircle, AlertTriangle, Phone, Calendar, Wrench, Shield, Clock } from 'lucide-react';

const DrivelineDifferential = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  const vehicles = [
    {
      title: 'BMW X5 xDrive (E70/F15)',
      years: '2007-2018',
      issues: 'Transfer case actuator motor failure, CV joint wear, rear differential issues',
      image: '/assets/assets/images/new/driveline-repair/30driveline-repair-european-vehicle-dallas.webp'
    },
    {
      title: 'Audi S4 Quattro (B8/B9)',
      years: '2009-2020',
      issues: 'Center differential wear, CV joint failure, Haldex coupling issues',
      image: '/assets/assets/images/new/driveline-repair/28driveline-repair-european-vehicle-dallas.webp'
    },
    {
      title: 'Mercedes-Benz ML 4MATIC (W164/W166)',
      years: '2006-2019',
      issues: 'Transfer case chain stretch, front differential mount failure, CV joint boot degradation',
      image: '/assets/assets/images/new/driveline-repair/32driveline-repair-european-vehicle-dallas.webp'
    },
    {
      title: 'Porsche 911 Carrera 4 (991/992)',
      years: '2012-Present',
      issues: 'PTM clutch pack wear, CV joint replacement, differential fluid degradation',
      image: '/assets/assets/images/new/driveline-repair/18driveline-repair-european-vehicle-dallas.webp'
    },
    {
      title: 'Volkswagen Golf R AWD (Mk7/Mk8)',
      years: '2015-Present',
      issues: 'Haldex pump failure, rear differential service, CV joint wear',
      image: '/assets/assets/images/new/driveline-repair/21driveline-repair-european-vehicle-dallas.webp'
    },
    {
      title: 'Land Rover Range Rover (L405/L460)',
      years: '2013-Present',
      issues: 'Transfer case failure, front differential leak, driveshaft joint wear',
      image: '/assets/assets/images/new/driveline-repair/10driveline-repair-european-vehicle-dallas.webp'
    }
  ];

  const services = [
    {
      title: 'Differential Service & Repair',
      items: [
        'Front and rear differential fluid service',
        'Limited slip differential repair',
        'Ring and pinion replacement',
        'Differential bearing replacement',
        'Electronic differential diagnostics'
      ]
    },
    {
      title: 'CV Joint & Axle Service',
      items: [
        'CV joint replacement and repair',
        'CV boot replacement and regreasing',
        'Drive axle replacement',
        'Half shaft balancing',
        'Constant velocity joint diagnostics'
      ]
    },
    {
      title: 'Transfer Case Service',
      items: [
        'Transfer case fluid replacement',
        'Chain and gear inspection',
        'Actuator motor replacement',
        'Electronic control module repair',
        'Viscous coupling service'
      ]
    },
    {
      title: 'All-Wheel Drive Systems',
      items: [
        'Haldex coupling service and repair',
        'AWD system diagnostics',
        'Clutch pack replacement',
        'Electronic control calibration',
        'Torque distribution optimization'
      ]
    }
  ];

  const warningSignsList = [
    { text: 'Vibration While Driving:', detail: 'Noticeable shaking, especially during acceleration' },
    { text: 'Clicking Sounds:', detail: 'CV joint noise during turns, particularly when parking' },
    { text: 'Grinding or Whining:', detail: 'Differential or transfer case bearing wear' },
    { text: 'Fluid Leaks:', detail: 'Differential, transfer case, or CV joint boot leakage' },
    { text: 'AWD System Warnings:', detail: 'Dashboard alerts for all-wheel drive malfunctions' },
    { text: 'Handling Issues:', detail: 'Uneven tire wear or directional pulling' },
    { text: 'Power Loss:', detail: 'Reduced acceleration or inability to maintain speed' },
    { text: 'Tire Wear Patterns:', detail: 'Unusual or accelerated tire wear indicating driveline problems' }
  ];

  const faqs = [
    {
      question: 'How often should I service my European AWD system?',
      answer: 'Most European AWD systems require differential fluid changes every 40,000-60,000 miles, with transfer case service at similar intervals. Haldex systems need more frequent service every 20,000-30,000 miles. Severe driving conditions may require more frequent maintenance.'
    },
    {
      question: 'What causes CV joint failure in European vehicles?',
      answer: 'CV joint wear typically results from torn boots allowing contamination, high mileage, aggressive driving, or suspension modifications. European vehicles with performance-oriented suspensions may experience accelerated CV joint wear due to increased operating angles and loads.'
    },
    {
      question: 'Can I drive with a damaged CV joint?',
      answer: 'While short distances may be possible, continued operation with a damaged CV joint can lead to complete failure and loss of power to that wheel. Clicking sounds during turns indicate immediate replacement is necessary to prevent being stranded.'
    },
    {
      question: 'Why is my AWD system warning light illuminated?',
      answer: 'AWD warning lights can indicate various issues from low fluid levels to electronic control module faults. European systems monitor multiple parameters and require professional diagnosis to identify the specific problem and prevent expensive component damage.'
    },
    {
      question: "What's the difference between AWD and 4WD systems?",
      answer: "AWD systems operate automatically and continuously, adjusting torque distribution as needed. European AWD systems are typically more sophisticated, using electronic controls and variable torque split for optimal performance in all conditions without driver intervention."
    }
  ];

  return (
    <>
      <Helmet>
        <title>European Driveline & Differential Repair Dallas | Paradise-Performance</title>
        <meta name="description" content="Expert differential and driveline repair for BMW xDrive, Audi Quattro, Mercedes 4MATIC. CV joints, transfer cases, AWD systems. Dallas European specialists." />
        <link rel="canonical" href="https://paradise-performance.com/services/driveline-differential" />
        
        {/* Open Graph */}
        <meta property="og:title" content="European Driveline & Differential Repair Dallas" />
        <meta property="og:description" content="Specialized driveline and differential service for European vehicles. BMW xDrive, Audi Quattro, Mercedes 4MATIC experts." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://paradise-performance.com/services/driveline-differential" />
      </Helmet>

      {/* Hero Section */}
      <div className="relative h-[600px] bg-gradient-to-b from-gray-900 to-gray-800">
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-40"
          style={{ backgroundImage: `url('/wp-content/uploads/2025/05/29driveline-repair-european-vehicle-dallas.webp')` }}
        />
        <div className="relative h-full flex items-center justify-center text-white">
          <div className="text-center px-4 max-w-4xl">
            <h1 className="text-5xl md:text-6xl font-bold mb-4 uppercase tracking-wide">
              European Driveline<br />& Differential Repair
            </h1>
            <p className="text-xl mb-2">BMW xDrive, Audi Quattro, Mercedes 4MATIC Specialists</p>
            <p className="text-lg mb-8 max-w-3xl mx-auto">
              Expert diagnosis and repair of advanced all-wheel drive systems, differentials, CV joints, 
              and transfer cases. Specialized service for BMW, Mercedes, Audi, Porsche, and all premium 
              European driveline systems in Dallas.
            </p>
            <Link 
              to="/service" 
              className="inline-block bg-red-600 hover:bg-red-700 text-white px-8 py-4 rounded font-semibold text-lg transition-colors"
            >
              Schedule Driveline Inspection
            </Link>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="bg-gray-50 py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Main Content */}
            <div className="lg:col-span-2">
              {/* Advanced Service Section */}
              <motion.section {...fadeInUp} className="mb-16">
                <h2 className="text-3xl font-bold mb-6 text-gray-900">Advanced European Driveline Service</h2>
                <p className="text-lg text-gray-700 mb-4">
                  European vehicles feature some of the most sophisticated all-wheel drive and differential 
                  systems in the automotive world. At Paradise-Performance, our technicians possess the 
                  specialized knowledge and equipment necessary to properly diagnose, service, and repair 
                  these complex driveline systems.
                </p>
                <p className="text-gray-700">
                  From BMW's intelligent xDrive technology to Audi's legendary Quattro system, Mercedes' 
                  4MATIC, and Porsche's advanced all-wheel drive variants, we understand the intricate 
                  engineering that delivers superior traction, handling, and performance. Our facility is 
                  equipped with the specialized tools and diagnostic capabilities required for proper 
                  European driveline service.
                </p>
              </motion.section>

              {/* Warning Signs Section */}
              <motion.div {...fadeInUp} className="bg-red-50 border-l-4 border-red-600 p-8 rounded-lg mb-16">
                <h2 className="text-2xl font-bold mb-6 text-red-800 flex items-center">
                  <AlertTriangle className="mr-3" />
                  Critical Driveline Warning Signs
                </h2>
                <p className="text-gray-700 mb-6">
                  These symptoms indicate potential driveline issues requiring immediate professional attention:
                </p>
                <ul className="space-y-3">
                  {warningSignsList.map((item, index) => (
                    <li key={index} className="flex items-start">
                      <span className="text-red-600 mr-2 mt-1">⚙️</span>
                      <div>
                        <strong className="text-gray-900">{item.text}</strong> {item.detail}
                      </div>
                    </li>
                  ))}
                </ul>
              </motion.div>

              {/* Vehicles Section */}
              <motion.section {...fadeInUp} className="mb-16">
                <h2 className="text-3xl font-bold mb-6 text-gray-900">
                  European Vehicles with Advanced Driveline Systems
                </h2>
                <p className="text-lg text-gray-700 mb-8">
                  European manufacturers lead the industry in sophisticated all-wheel drive technology. 
                  These models require specialized expertise for proper driveline service and repair:
                </p>
                
                <div className="grid md:grid-cols-2 gap-6">
                  {vehicles.map((vehicle, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow"
                    >
                      <div className="h-48 overflow-hidden">
                        <img 
                          src={vehicle.image}
                          alt={vehicle.title}
                          className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                          loading="lazy"
                        />
                      </div>
                      <div className="p-6">
                        <h3 className="text-xl font-bold mb-2 text-gray-900">{vehicle.title}</h3>
                        <p className="text-red-600 font-semibold mb-2">Common Issues:</p>
                        <p className="text-gray-700 text-sm mb-3">{vehicle.issues}</p>
                        <p className="text-gray-900 font-semibold">Years: {vehicle.years}</p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </motion.section>

              {/* Services Grid */}
              <motion.section {...fadeInUp} className="mb-16">
                <h2 className="text-3xl font-bold mb-8 text-gray-900 text-center">
                  Specialized Driveline & Differential Services
                </h2>
                <div className="grid md:grid-cols-2 gap-6">
                  {services.map((service, index) => (
                    <div key={index} className="bg-white p-6 rounded-lg shadow-lg border hover:border-red-500 transition-colors">
                      <h3 className="text-xl font-bold mb-4 text-red-600">{service.title}</h3>
                      <ul className="space-y-2">
                        {service.items.map((item, idx) => (
                          <li key={idx} className="flex items-start">
                            <CheckCircle className="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                            <span className="text-gray-700">{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </motion.section>

              {/* FAQ Section */}
              <motion.section {...fadeInUp} className="mb-16">
                <h2 className="text-3xl font-bold mb-8 text-gray-900">Expert Answers to Your Questions</h2>
                <div className="space-y-6">
                  {faqs.map((faq, index) => (
                    <div key={index} className="border-b pb-6">
                      <h3 className="text-xl font-bold mb-3 text-gray-900">{faq.question}</h3>
                      <p className="text-gray-700">{faq.answer}</p>
                    </div>
                  ))}
                </div>
              </motion.section>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="sticky top-8 space-y-6">
                {/* Contact Card */}
                <div className="bg-white rounded-lg shadow-lg p-6">
                  <h3 className="text-xl font-bold mb-4 text-gray-900 border-b-2 border-red-600 pb-2">
                    Why Choose Paradise-Performance
                  </h3>
                  <ul className="space-y-3 mb-6">
                    <li className="flex items-start">
                      <span className="text-red-600 mr-2">🔧</span>
                      <span className="text-gray-700">Factory-trained European specialists</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-red-600 mr-2">🔧</span>
                      <span className="text-gray-700">Advanced AWD diagnostic equipment</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-red-600 mr-2">🔧</span>
                      <span className="text-gray-700">OEM parts and specifications</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-red-600 mr-2">🔧</span>
                      <span className="text-gray-700">Warranty on all repairs</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-red-600 mr-2">🔧</span>
                      <span className="text-gray-700">20-30% less than dealerships</span>
                    </li>
                  </ul>
                  <Link 
                    to="/service"
                    className="block w-full bg-red-600 hover:bg-red-700 text-white text-center py-3 rounded font-semibold transition-colors"
                  >
                    Book Service
                  </Link>
                </div>

                {/* Brands Card */}
                <div className="bg-gray-100 rounded-lg p-6">
                  <h3 className="text-xl font-bold mb-4 text-gray-900 border-b-2 border-red-600 pb-2">
                    We Service All European AWD Systems
                  </h3>
                  <div className="grid grid-cols-2 gap-3">
                    {['BMW xDrive', 'Audi Quattro', 'Mercedes 4MATIC', 'Porsche AWD', 'VW 4MOTION', 'Jaguar AWD'].map((brand) => (
                      <div key={brand} className="bg-white p-3 rounded text-center hover:bg-red-600 hover:text-white transition-colors text-sm font-medium">
                        {brand}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Emergency Service */}
                <div className="bg-red-600 text-white rounded-lg p-6 text-center">
                  <h3 className="text-xl font-bold mb-4">Emergency Driveline Service</h3>
                  <p className="mb-4">Strange noises or vibrations? Don't risk further damage.</p>
                  <a 
                    href="tel:8173822316" 
                    className="flex items-center justify-center bg-white text-red-600 py-3 rounded font-bold hover:bg-gray-100 transition-colors"
                  >
                    <Phone className="mr-2" />
                    (817) 382-2316
                  </a>
                </div>

                {/* Related Services */}
                <div className="bg-white rounded-lg shadow-lg p-6">
                  <h3 className="text-xl font-bold mb-4 text-gray-900 border-b-2 border-red-600 pb-2">
                    Related Services
                  </h3>
                  <div className="space-y-3">
                    <Link to="/services/transmission-repair" className="block hover:text-red-600 transition-colors">
                      → Transmission Repair
                    </Link>
                    <Link to="/services/suspension-repair" className="block hover:text-red-600 transition-colors">
                      → Suspension Service
                    </Link>
                    <Link to="/services/european-car-repair" className="block hover:text-red-600 transition-colors">
                      → European Car Repair
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* CTA Section */}
          <motion.div {...fadeInUp} className="bg-gradient-to-r from-gray-900 to-gray-800 rounded-lg p-12 text-white text-center mt-16">
            <h3 className="text-3xl font-bold mb-4 text-red-500">
              Is Your European AWD System Operating Optimally?
            </h3>
            <p className="text-xl mb-8 max-w-3xl mx-auto">
              Don't compromise your vehicle's traction, handling, and safety. Our comprehensive 
              driveline inspection can identify worn components before they lead to expensive 
              failures, ensuring your European vehicle delivers the performance it was designed for.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="tel:8173822316"
                className="inline-flex items-center justify-center bg-white text-gray-900 px-8 py-3 rounded font-semibold hover:bg-gray-100 transition-colors"
              >
                <Phone className="mr-2" />
                Call (817) 382-2316
              </a>
              <Link 
                to="/service"
                className="inline-flex items-center justify-center bg-red-600 text-white px-8 py-3 rounded font-semibold hover:bg-red-700 transition-colors"
              >
                <Calendar className="mr-2" />
                Schedule Driveline Service
              </Link>
            </div>
          </motion.div>
        </div>
      </div>
    </>
  );
};

export default DrivelineDifferential;
